import createDataContext from '@src/context/createDataContext';
import { HOST, makeGetApiRequest, makePostApiRequest } from '@common/index';
import axios from 'axios';
import { showAlert } from '@common/helper';
import { saveEvidenceRequest, undoEvidenceRequest, viewEvidenceCall } from './EvidenceAPI';

/** Standard part map API: one part entry (name → category_id + images). */
export interface StandardPartMapEntry {
    category_id: number;
    images: string[];
}

/** Per-node map: part name → StandardPartMapEntry. */
export type StandardPartMapNodeData = Record<string, StandardPartMapEntry>;

/** get-standard-part-name-and-photo API response. */
export interface StandardPartMapResponse {
    incompatible_nodes: Record<string, unknown>;
    message: string;
    nodes_data: Record<string, StandardPartMapNodeData>;
    status_code: number;
}

// Cache for get-standard-part-name-and-photo: avoids duplicate API calls when
// variantId, nodeIds, requestSlug, and refurbCarId are unchanged. Reset on full reload.
let lastStdPartMapRequestKey: string | null = null;
let lastStdPartMapResponse: StandardPartMapResponse | null = null;

// Cache for get-spare-part-data: avoids duplicate API calls when
// cityTeamJobListId, nodeIds, and showPreviouslyApprovedQuantity are unchanged.
let lastSparePartRequestKey: string | null = null;
let lastSparePartResponse: any = null;

interface StateType {
    budgetListData: any;
    budgetDetails: any;
    allFixes: any;
    filterListing: any;
    recommendedLC: any;
    budgetMicroJobRow: any;
    microCohortData: any;
    historyData: any;
    recommendedPC: any;
    stdPartMap: StandardPartMapResponse | Record<string, never>;
    sparesHistory: any;
}

interface ActionType {
    payload: any,
    type: string
}

const initialState: StateType = {
    budgetListData: [],
    budgetDetails: {},
    allFixes: [],
    filterListing: {},
    recommendedLC: {},
    budgetMicroJobRow: {},
    microCohortData: {},
    historyData: [],
    sparesHistory:[],
    recommendedPC: {},
    stdPartMap: {}
}

const reducer = (state, action: ActionType) => {

    switch (action.type) {
        case 'setBudgetListData': return { ...state, budgetListData: action.payload }

        case 'setBudgetDetails': return { ...state, budgetDetails: action.payload }

        case 'setAllFixes': return { ...state, allFixes: action.payload }

        case 'setFilterListing': return { ...state, filterListing: action.payload }

        case 'setRecommendedLC': return { ...state, recommendedLC: action.payload }

        case 'setBudgetMicroJobRow': return { ...state, budgetMicroJobRow: action.payload }

        case 'setMicroCohortData': return { ...state, microCohortData: action.payload }

        case 'setHistoryData': return { ...state, historyData: action.payload }

        case 'setRecommendedPC': return { ...state, recommendedPC: action.payload }

        case 'setStdPartMap': return { ...state, stdPartMap: action.payload }

        case 'setSparesHistory': return { ...state, sparesHistory: action.payload }

        default: return state
    }

}

const setBudgetListData = dispatch => {

    return async (variables, getBudgetListData, callback?: (data?) => void, err_callback?: (message) => void) => {

        try {

            const response = await getBudgetListData({ variables })
            dispatch({ type: 'setBudgetListData', payload: response.data?.getJobListListingForBudgetApproverV3 })

            !!callback ? callback?.(response.data?.getJobListListingForBudgetApproverV3) : null

        } catch (e) {
            console.log('GET BUDGET DATA ERROR', e)
            !!err_callback ? err_callback?.(e?.message) : null
        }

    }

}

// Builds a stable key for the standard part map request. Node IDs are sorted so
// [1,2,3] and [3,2,1] produce the same key and reuse cache.
function getStdPartMapRequestKey(variables: { variantId: any; nodeIds: any[]; requestSlug?: string; refurbCarId?: string | null }): string {
    const { variantId, nodeIds, requestSlug, refurbCarId } = variables;
    const sortedNodeIds = [...(nodeIds || [])].sort((a, b) => Number(a) - Number(b));
    return JSON.stringify({ variantId, requestSlug, refurbCarId, nodeIds: sortedNodeIds });
}

/**
 * Fetches standard part names and photos for the given variant and node IDs.
 * If the same request key (variantId, nodeIds, requestSlug, refurbCarId) was already
 * fetched, calls callback with cached data and does not call the API.
 * On first request or when the key changes, calls the API, updates context and
 * cache, then invokes callback.
 */
const setStandardPartMapData = dispatch => {

    return async (variables, callback?: (data: StandardPartMapResponse) => void, err_callback?: (message: string) => void) => {

        try {
            let { variantId, nodeIds, requestSlug, refurbCarId } = variables;

            // Reuse cached response if request params are unchanged.
            const currentKey = getStdPartMapRequestKey({ variantId, nodeIds, requestSlug, refurbCarId });
            if (currentKey === lastStdPartMapRequestKey && lastStdPartMapResponse != null) {
                // Same node set and params — skip API and run callback with cached data.
                !!callback ? callback?.(lastStdPartMapResponse) : null;
                return;
            }

            // Params changed or no cache — call API and update cache on success.
            let reqBody = {
                variant_id: Number(variantId),
                node_ids: nodeIds,
                request_slug: requestSlug,
                refurb_car_id: refurbCarId || null
            };
            if (!requestSlug) {
                delete reqBody.request_slug;
            }
            const response = await makePostApiRequest(`${HOST}/part-management/get-standard-part-name-and-photo`, reqBody, {}, 'Keycloak-Authorization', 'Bearer', 'refurb');

            // Update cache so future identical requests skip the API.
            lastStdPartMapRequestKey = currentKey;
            lastStdPartMapResponse = response.data;

            // Updates context state for consumers that read state.stdPartMap.
            dispatch({ type: 'setStdPartMap', payload: response.data });

            !!callback ? callback?.(response.data) : null;

        } catch (e) {
            console.log('Get standard par name ERROR', e);
            showAlert(e?.response?.data?.message, 'error');
            !!err_callback ? err_callback?.(e?.message) : null;
        }

    };

};

// Builds a stable key for the spare part data request. Node IDs are sorted so
// [1,2,3] and [3,2,1] produce the same key and reuse cache.
function getSparePartRequestKey(variables: { cityTeamJobListId: any; nodeIds: any[]; showPreviouslyApprovedQuantity?: boolean }): string {
    const { cityTeamJobListId, nodeIds, showPreviouslyApprovedQuantity } = variables;
    const sortedNodeIds = [...(nodeIds || [])].sort((a, b) => Number(a) - Number(b));
    return JSON.stringify({ cityTeamJobListId, showPreviouslyApprovedQuantity, nodeIds: sortedNodeIds });
}

/**
 * Fetches spare part data for the given job list and node IDs.
 * If the same request key (cityTeamJobListId, nodeIds, showPreviouslyApprovedQuantity) was already
 * fetched, calls callback with cached data and does not call the API.
 * On first request or when the key changes, calls the API, updates cache, then invokes callback.
 */
const setSparePartData = dispatch => {

    return async (variables, callback?: (data?) => void, err_callback?: (message) => void) => {

        try {
            let { cityTeamJobListId, nodeIds, showPreviouslyApprovedQuantity } = variables;

            // Reuse cached response if request params are unchanged.
            const currentKey = getSparePartRequestKey({ cityTeamJobListId, nodeIds, showPreviouslyApprovedQuantity });
            if (currentKey === lastSparePartRequestKey && lastSparePartResponse != null) {
                !!callback ? callback?.(lastSparePartResponse) : null;
                return;
            }

            const response = await makePostApiRequest(`${HOST}/part-management/get-spare-part-data`, {
                job_list_id: cityTeamJobListId,
                node_ids: nodeIds,
                show_previously_approved_quantity: showPreviouslyApprovedQuantity,
            }, {}, 'Keycloak-Authorization', 'Bearer', 'refurb');

            lastSparePartRequestKey = currentKey;
            lastSparePartResponse = response.data;

            !!callback ? callback?.(response.data) : null;

        } catch (e) {
            console.log('Get spare par data ERROR', e);
            showAlert(e?.response?.data?.message, 'error');
            !!err_callback ? err_callback?.(e?.message) : null;
        }

    };

};

const setBudgetDetails = dispatch => {

    return async (variables, getBudgetDetails, callback?: (data?) => void, err_callback?: (message) => void) => {

        try {

            const response = await getBudgetDetails({ variables })
            dispatch({ type: 'setBudgetDetails', payload: response.data?.getJobList })

            !!callback ? callback?.(response.data?.getJobList) : null

        } catch (e) {
            console.log('GET BUDGET DETAILS ERROR', e)
            !!err_callback ? err_callback?.(e?.message) : null
        }

    }

}

const setAllFixes = dispatch => {

    return async (variables, getAllFixes, callback?: (data?) => void, err_callback?: (message) => void) => {

        try {

            // const response = await getAllFixes({ variables })
            // dispatch({ type: 'setAllFixes', payload: response.data?.getAllFixes })

            var response = await fetch(`${ HOST }/get/carpart-fixes?car_part_id=${ variables?.carPartId }&car_part_name=${ variables?.carPartName }`, {
                method: 'GET',
                headers: {
                    "Keycloak-Authorization": `Bearer ${localStorage.getItem("token")}`,
                    "auth-type": "Keycloak",
                },
            })
            var responseJson = await response?.json?.()
            if(responseJson?.data?.length === 0){
                showAlert(`Error: ${responseJson?.message} `, "error");
            }
            responseJson = responseJson?.data?.map?.((val) => ({ id: val?.fix_id, name: val?.fix__name, isActionable: true, isPartRequired: val?.fix__name?.toLowerCase?.()?.includes?.('replace') }))
            dispatch({ type: 'setAllFixes', payload: responseJson })

            !!callback ? callback?.(responseJson) : null

        } catch (e) {
            console.log('GET ALL FIXES ERROR', e)
            !!err_callback ? err_callback?.(e?.message) : null
        }

    }

}

const updateFix = dispatch => {

    return async (variables, updateFixMutation, callback?: (data?) => void, err_callback?: (message) => void) => {

        try {

            const response = await updateFixMutation({ variables })
            !!callback ? callback?.(response.data?.approveJobRowByBudgetApprover) : null

        } catch (e) {
            console.log('GET UPDATE FIX ERROR', e)
            !!err_callback ? err_callback?.(e?.message) : null
        }

    }

}

const submitApproval = dispatch => {

    return async (variables, submitMutation, callback?: (data?) => void, err_callback?: (message) => void) => {

        try {

            const response = await submitMutation({ variables })
            !!callback ? callback?.(response.data?.approveJobRowByBudgetApprover) : null

        } catch (e) {
            console.log('GET SUBMIT APPROVAl ERROR', e)
            !!err_callback ? err_callback?.(e?.message) : null
        }

    }

}

const setFilterListing = dispatch => {

    return async (variables, callback?: (data?) => void, err_callback?: (message) => void) => {

        try {
            let { workshopId } = variables;
            const url = `${HOST}/filters/get-job-list-listing-for-budget-approver/v2?workshop_id=${workshopId}`;
            const response = await makeGetApiRequest(url, {}, 'Keycloak-Authorization', 'Bearer', 'refurb');
            dispatch({ type: 'setFilterListing', payload: response.data })

            !!callback ? callback?.(response.data) : null

        } catch (e) {
            console.log('GET FILTER LISTING ERROR', e)
            !!err_callback ? err_callback?.(e?.message) : null
        }

    }

}

const setRecommendedLC = dispatch => {

    return async (variables, callback?: (data?) => void, err_callback?: (message) => void) => {

        try {
            let { jobListId, workshopId } = variables;
            const response = await axios.get(`${HOST}/get/labour_cost?job_list_id=${jobListId}&workshop_id=${workshopId}`, {
                headers: {
                    "Keycloak-Authorization": `Bearer ${localStorage.getItem("token")}`,
                    "auth-type": "Keycloak",
                },
            });
            dispatch({ type: 'setRecommendedLC', payload: response.data })

            !!callback ? callback?.(response.data) : null

        } catch (e) {
            console.log('GET RECOMMENDED LC ERROR', e)
            !!err_callback ? err_callback?.(e?.message) : null
        }

    }

}

const setBudgetMicroJobRow = dispatch => {

    return async (variables, getBudgetMicroJobRow, callback?: (data?) => void, err_callback?: (message) => void) => {

        try {

            const response = await getBudgetMicroJobRow({ variables })
            dispatch({ type: 'setBudgetMicroJobRow', payload: response.data?.getJobCardCityTeamV2 })

            !!callback ? callback?.(response.data?.getJobCardCityTeamV2) : null

        } catch (e) {
            console.log('GET MICRO JOB ROW ERROR', e)
            !!err_callback ? err_callback?.(e?.message) : null
        }

    }

}

const submitApprovalMicroJob = dispatch => {

    return async (variables, submitMutationMicroJob, callback?: (data?) => void, err_callback?: (message) => void) => {

        try {

            const response = await submitMutationMicroJob({ variables })
            !!callback ? callback?.(response.data?.budgetApproveMicroJobRows) : null

        } catch (e) {
            console.log('GET SUBMIT APPROVAl MICRO ERROR', e)
            !!err_callback ? err_callback?.(e?.message) : null
        }

    }

}

const updateFixMicroJob = dispatch => {

    return async (variables, updateFixMicroMutation, callback?: (data?) => void, err_callback?: (message) => void) => {

        try {

            const response = await updateFixMicroMutation({ variables })
            !!callback ? callback?.(response.data?.budgetApproveMicroJobRowFix) : null

        } catch (e) {
            console.log('GET UPDATE FIX MICRO ERROR', e)
            !!err_callback ? err_callback?.(e?.message) : null
        }

    }

}

const getMicroCohortData = dispatch => {

    return async (variables, callback?: (data?) => void, err_callback?: (message) => void) => {

        try {
            let { jobListId } = variables;
            const response = await axios.post(`${HOST}/get-micro-cohort`, {
                job_list_id: jobListId
            });
            dispatch({ type: 'setMicroCohortData', payload: response.data })

            !!callback ? callback?.(response.data) : null

        } catch (e) {
            console.log('GET MICRO COHORT ERROR', e)
            showAlert(e?.response?.data?.message, 'error')
            !!err_callback ? err_callback?.(e?.message) : null
        }

    }

}

const getHistoryData = dispatch => {

    return async (variables, callback?: (data?) => void, err_callback?: (message) => void) => {

        try {
            let { jobListId } = variables;
            const response = await axios.get(`${HOST}/get/fix_history/${jobListId}`, {
                headers: {
                    "Keycloak-Authorization": `Bearer ${localStorage.getItem("token")}`,
                    "auth-type": "Keycloak",
                },
            });
            dispatch({ type: 'setHistoryData', payload: response?.data?.data })

            !!callback ? callback?.(response?.data?.data) : null

        } catch (e) {
            console.log('GET HISTORY ERROR', e)
            !!err_callback ? err_callback?.(e?.message) : null;
        }

    }

}

const getSparesHistory = dispatch => {

    return async (variables, callback?: (data?) => void, err_callback?: (message) => void) => {

        try {
            let { cityTeamJobListId, orderBy } = variables;
            const response = await makePostApiRequest(`${HOST}/part-management/get-spares-history`, {
                job_list_id: cityTeamJobListId,
                order_by: orderBy
            }, {}, 'Keycloak-Authorization', 'Bearer', 'refurb');
            dispatch({ type: 'setSparesHistory', payload: response?.data?.data })

            !!callback ? callback?.(response?.data?.data) : null

        } catch (e) {
            console.log('GET HISTORY ERROR', e)
            !!err_callback ? err_callback?.(e?.message) : null;
        }

    }

}

const setRecommendedPC = dispatch => {

    return async (variables, partCostMutation, callback?: (data?) => void, err_callback?: (message) => void) => {

        try {

            const response = await partCostMutation({ variables });
            dispatch({ type: 'setRecommendedPC', payload: response?.data?.getEstimatePartCostForNode })

            !!callback ? callback?.(response?.data?.getEstimatePartCostForNode) : null

        } catch (e) {
            console.log('GET PART COST MAPPING ERROR', e)
            !!err_callback ? err_callback?.(e?.message) : null
        }

    }

}

export interface BudgetInterface {
    state: StateType,
    setBudgetListData: (variables, getBudgetListData, callback?: (data?) => void, err_callback?: (message) => void) => Promise<void>,
    setBudgetDetails: (variables, getBudgetDetails, callback?: (data?) => void, err_callback?: (message) => void) => Promise<void>,
    setAllFixes: (variables, getAllFixes, callback?: (data?) => void, err_callback?: (message) => void) => Promise<void>,
    updateFix: (variables, updateFixMutation, callback?: (data?) => void, err_callback?: (message) => void) => Promise<void>,
    submitApproval: (variables, submitMutation, callback?: (data?) => void, err_callback?: (message) => void) => Promise<void>,
    setBudgetMicroJobRow: (variables, getBudgetMicroJobRow, callback?: (data?) => void, err_callback?: (message) => void) => Promise<void>,
    updateFixMicroJob: (variables, updateFixMicroMutation, callback?: (data?) => void, err_callback?: (message) => void) => Promise<void>,
    submitApprovalMicroJob: (variables, submitMutationMicroJob, callback?: (data?) => void, err_callback?: (message) => void) => Promise<void>,
    setFilterListing: (variables, callback?: (data?) => void, err_callback?: (message) => void) => Promise<void>,
    setRecommendedLC: (variables, callback?: (data?) => void, err_callback?: (message) => void) => Promise<void>,
    getMicroCohortData: (variables, callback?: (data?) => void, err_callback?: (message) => void) => Promise<void>,
    getHistoryData: (variables, callback?: (data?) => void, err_callback?: (message) => void) => Promise<void>,
    getSparesHistory: (variables, callback?: (data?) => void, err_callback?: (message) => void) => Promise<void>,
    saveEvidenceRequest: (variables, callback?: (data?) => void, err_callback?: (message) => void) => Promise<void>,
    undoEvidenceRequest: (variables, callback?: (data?) => void, err_callback?: (message) => void) => Promise<void>,
    viewEvidenceCall: (variables, callback?: (data?) => void, err_callback?: (message) => void) => Promise<void>,
    setRecommendedPC: (variables, partCostMutation, callback?: (data?) => void, err_callback?: (message) => void) => Promise<void>,
    setStandardPartMapData: (variables, callback?: (data?) => void, err_callback?: (message) => void) => Promise<void>,
    setSparePartData: (variables, callback?: (data?) => void, err_callback?: (message) => void) => Promise<void>,
}

export const { Context: BudgetContext, Provider: BudgetProvider } = createDataContext(reducer,
    {   
        setBudgetListData,
        setBudgetDetails,
        setAllFixes, 
        updateFix,
        submitApproval,
        setFilterListing,
        setRecommendedLC,
        setBudgetMicroJobRow,
        submitApprovalMicroJob,
        updateFixMicroJob,
        getMicroCohortData,
        getHistoryData,
        setRecommendedPC,
        saveEvidenceRequest,
        undoEvidenceRequest,
        viewEvidenceCall,
        setStandardPartMapData,
        setSparePartData,
        getSparesHistory
    },
    initialState)
